import React, { useEffect, useMemo, useState } from "react";
import { Button, Form, Alert, Spinner, InputGroup, Card, Row, Col, Image, Modal } from "react-bootstrap";
import {editAssignment, editAssignmentAdmin} from "../../http/bookAPI";
import { fetchReviewDataFromExtension } from "../../helpers/extensionScrape";

/**
 * Админамазонревьюпарсерекст
 *
 * Пропсы:
 * - assignmentData: объект ассигмента (обязательно: id, assTypeId, booksTypeId, book{...}, reviewer_name, rating и т.п.)
 * - onSaved?: (message: string, response?: any) => (void | Promise<void>)  // вызвать после успешного сохранения, чтобы родитель перечитал данные
 * - defaultLink?: string            // опциональный стартовый линк для поля ввода
 */
const AdminAmazonReviewParserExt = ({ assignmentData, onSaved, defaultLink }) => {
    // --- UI state ---
    const [inputUrl, setInputUrl] = useState(defaultLink || assignmentData?.review_link || "");
    const [loading, setLoading] = useState(false);
    const [serverMsg, setServerMsg] = useState(null);     // { variant: "success" | "danger" | "warning" , text: string }
    const [error, setError] = useState("");               // локальная ошибка submit
    const [reviewData, setReviewData] = useState(null);   // полученные из Amazon данные (без записи при Submit)
    const [showAsinOverrideConfirm, setShowAsinOverrideConfirm] = useState(false);

    // синхронизация поля при обновлении пропса
    useEffect(() => {
        if (typeof defaultLink === "string" && defaultLink.trim()) setInputUrl(defaultLink);
        else if (typeof assignmentData?.review_link === "string") setInputUrl(assignmentData.review_link);
    }, [defaultLink, assignmentData?.review_link]);

    // --- helpers из текущей админки (адаптация из EditAssignmentAdmin) ---
    const BOOK_TYPE_LABEL = { 1: "Kindle", 2: "Paperback", 3: "Hardcover", 4: "Audio" };

    const getExpectedAsinByType = (booksTypeId, book) => {
        if (!book) return null;
        switch (Number(booksTypeId)) {
            case 1: return book.kindle_asin || null;
            case 2: return book.paper_back_asin || null;
            case 3: return book.hard_cover_asin || null;
            case 4: return book.audio_asin || book.audio_audible_asin || null;
            default: return null;
        }
    };

    const isAsinOfThisBook = (asin, book) => {
        if (!asin || !book) return false;
        const list = [
            book.kindle_asin,
            book.paper_back_asin,
            book.hard_cover_asin,
            book.audio_asin,
            book.audio_audible_asin,
        ].filter(Boolean);
        return list.includes(asin);
    };

    const normalize = (s) =>
        (s || "")
            .normalize("NFKD")
            .replace(/\p{M}/gu, "")
            .toLowerCase()
            .replace(/[^\p{L}\p{N}]+/gu, "");

    const expectsVerified = (assTypeId) => {
        const MUST_BE_VERIFIED = new Set([3,4,5,6,12,13,16,17,18,19,20,21,24,25,26,27]);
        return MUST_BE_VERIFIED.has(Number(assTypeId));
    };

    const isVideoUrl = (u = "") =>
        /\.mp4(\?|$)/i.test(u) || u.includes("/vse-vms-transcoding-artifact-");

    const renderMediaThumb = (url, i) => {
        if (!url) return null;
        if (isVideoUrl(url)) {
            return (
                <video
                    key={`media-${i}`}
                    src={url}
                    preload="metadata"
                    controls
                    className="shadow rounded"
                    style={{ width: 180, borderRadius: 6 }}
                    onError={(e) => {
                        e.currentTarget.outerHTML = `
              <img 
                src="${process.env.REACT_APP_API_URL}covers/empty-cover.jpg" 
                style="width:160px; height:120px; object-fit:cover; border-radius:6px" 
                class="shadow"
                alt="Fallback"
              />
            `;
                    }}
                />
            );
        }
        return (
            <Image
                key={`media-${i}`}
                src={url}
                alt={`Review Media ${i + 1}`}
                className="shadow"
                style={{ width: 180, objectFit: "cover", borderRadius: 6 }}
                onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = `${process.env.REACT_APP_API_URL}covers/empty-cover.jpg`;
                }}
                onClick={() => window.open(url, "_blank")}
            />
        );
    };

    // --- анализ различий (как в EditAssignmentAdmin) ---
    const analysis = useMemo(() => {
        if (!reviewData || !assignmentData) return { mismatches: [], asinNote: null };

        const mismatches = [];
        let asinNote = null;

        const expASIN = getExpectedAsinByType(assignmentData?.booksTypeId, assignmentData?.book);
        const gotASIN = (reviewData?.asin || "").trim();
        const fromThisBook = isAsinOfThisBook(gotASIN, assignmentData?.book);

        if (gotASIN) {
            if (!fromThisBook) {
                mismatches.push({
                    field: "ASIN",
                    expected: expASIN || "(ASIN for selected book type)",
                    got: gotASIN,
                    note: "ASIN does not belong to this book.",
                });
            } else if (expASIN && gotASIN !== expASIN) {
                asinNote = `ASIN is from this book but a different format: expected ${BOOK_TYPE_LABEL[assignmentData?.booksTypeId]} (${expASIN}), got ${gotASIN}.`;
            }
        }

        const expName = normalize(assignmentData?.reviewer_name);
        const gotName = normalize(reviewData?.review_name);
        if (expName && gotName && expName !== gotName) {
            mismatches.push({
                field: "Reviewer name",
                expected: assignmentData?.reviewer_name,
                got: reviewData?.review_name,
                note: "Names differ.",
            });
        }

        const needVerified = expectsVerified(assignmentData?.assTypeId);
        const gotVerified = /verified/i.test(reviewData?.review_status || "");
        if (needVerified !== gotVerified) {
            mismatches.push({
                field: "Verification",
                expected: needVerified ? "Verified Purchase" : "Unverified allowed",
                got: reviewData?.review_status || "—",
                note: "Verification status does not match assignment requirements.",
            });
        }

        const expRating = Number(assignmentData?.rating);
        const gotRating = Number(reviewData?.review_rating);
        if (!Number.isNaN(expRating) && !Number.isNaN(gotRating) && expRating > 0 && gotRating > 0 && expRating !== gotRating) {
            mismatches.push({
                field: "Rating",
                expected: `${expRating}★`,
                got: `${gotRating}★`,
                note: "Rating differs.",
            });
        }

        return { mismatches, asinNote };
    }, [reviewData, assignmentData]);

    // --- действия ---
    const fetchReview = async () => {
        const url = (inputUrl || "").trim();
        if (!url) {
            setError("Please enter a valid Amazon review URL.");
            return;
        }
        setLoading(true);
        setError("");
        setServerMsg(null);

        try {
            // 1) через расширение (как в AmazonReviewParserExt)
            const data = await fetchReviewDataFromExtension(url, { timeoutMs: 15000 });
            setReviewData(data);              // показать полный блок (без записи)
        } catch (extErr) {
                console.error("Admin review extension checker failed:", extErr);
                setError(
                    "Great, we got your link. We couldn’t check it automatically, but we will review it manually."
                );
        } finally {
            setLoading(false);
        }
    };

    const saveToDb = async ({ staffAsinOverride = false } = {}) => {
        if (!assignmentData?.id) {
            setServerMsg({ variant: "danger", text: "Assignment ID is missing." });
            return;
        }
        if (!reviewData) {
            setServerMsg({ variant: "warning", text: "Nothing to save. Submit a review link first." });
            return;
        }

        setLoading(true);
        setServerMsg(null);

        try {
            // Поле-от-слова: маппинг как в EditAssignmentAdmin.handleExtractReview (checked_* + базовые)
            // (Важные поля: SUCCESS, STEP5, end_rev_date в минутах, review fields)
            const nowMinutes = () => Math.floor(Date.now() / 60000);
            const payload = {
                id: assignmentData.id,
                assignment_status: 'SUCCESS',
                end_rev_date: nowMinutes(),
                reviewer_reading_step: "STEP5",
                review_link: reviewData.review_link,
                checked_asin: reviewData.asin,
                checked_review_name: reviewData.review_name,
                checked_review_status: reviewData.review_status,
                checked_review_link: reviewData.review_link,
                checked_review_title: reviewData.review_title,
                checked_review_rating: reviewData.review_rating,
                checked_review_country: reviewData.review_country,
                checked_review_date: reviewData.review_date,
                checked_proxy_country: reviewData.proxy_country,
                checked_review_text: reviewData.review_text,
            };

            if (staffAsinOverride) {
                payload.staff_asin_override = true;
            }

// ✅ Отправляем фото только если у assignmentData нет своих
            if (!assignmentData.checked_review_photo)
                payload.checked_review_photo = reviewData.review_photo;

            if (!assignmentData.checked_review_photo2)
                payload.checked_review_photo2 = reviewData.review_photo2;

// Остальные фото без условий (если нужно — добавь аналогично)
            payload.checked_review_photo3 = reviewData.review_photo3;
            payload.checked_review_photo4 = reviewData.review_photo4;
            payload.checked_review_photo5 = reviewData.review_photo5;

            const write = await editAssignmentAdmin(payload);


            const serverText =
                write?.data?.message ||
                write?.message ||
                "Assignment updated successfully.";

            setServerMsg({ variant: "success", text: serverText });

            // перечитать в родителе, если нужно
            if (typeof onSaved === "function") {
                await onSaved(serverText, write);
            }
        } catch (error) {
            const errText =
                error?.response?.data?.message ||
                error?.response?.data?.error ||
                error?.message ||
                "Failed to update assignment";
            setServerMsg({ variant: "danger", text: errText });
        } finally {
            setLoading(false);
            setShowAsinOverrideConfirm(false);
        }
    };

    const saveGuards = useMemo(() => {
        // Нет данных — сразу нельзя сохранять
        if (!reviewData) {
            return {
                asinOk: false,
                nameOk: false,
                canSave: false,
                reason: "Submit a review link first."
            };
        }

        // 1) Проверка, что ASIN принадлежит этой книге (любой формат этой книги)
        const asinOk = isAsinOfThisBook((reviewData.asin || "").trim(), assignmentData?.book);

        // 2) Проверка совпадения имени (нормализуем, чтобы избежать артефактов пробелов/регистров)
        const nameOk = normalize(reviewData?.review_name) === normalize(assignmentData?.reviewer_name);

        // Итоговый флаг
        const canSave = asinOk && nameOk;
        const canApproveAsinMismatch = !asinOk && nameOk;

        // Причина (для tooltip)
        let reason = "";
        if (!asinOk) reason = "ASIN does not belong to this book.";
        else if (!nameOk) reason = "Reviewer name does not match the assignment.";

        return { asinOk, nameOk, canSave, canApproveAsinMismatch, reason };
    }, [reviewData, assignmentData]);

    const handleSaveClick = () => {
        if (!reviewData) return;
        if (saveGuards.canApproveAsinMismatch) {
            setShowAsinOverrideConfirm(true);
            return;
        }
        saveToDb();
    };

    // --- рендер ---
    return (
        <Card className="mt-2 mb-2 border border-secondary">
            <Card.Header className="py-1 small">Admin Amazon Review Parser Checker. Insert Link, Check Results then Save.</Card.Header>
            <Card.Body className="pt-2">
            {/* форма ввода URL */}
            <Form
                onSubmit={(e) => {
                    e.preventDefault();
                    fetchReview();
                }}
                className="small"
            >
                <InputGroup className="mb-2 mt-2 border-success" size="sm">
                    <Form.Control
                        type="text"
                        placeholder="Paste your Amazon review link…"
                        value={inputUrl}
                        onChange={(e) => setInputUrl(e.target.value)}
                        disabled={loading}
                        size="sm"
                        className="border-success"
                    />
                    <Button
                        variant="success"
                        onClick={fetchReview}
                        disabled={loading}
                        type="button"
                        size="sm"
                        className="px-2"
                        title="Get and analyze review data (no DB write)"
                    >
                        {loading ? <Spinner animation="border" size="sm" /> : "Submit"}
                    </Button>

                    <Button
                        variant="success"
                        onClick={handleSaveClick}
                        disabled={loading || !reviewData || (!saveGuards.canSave && !saveGuards.canApproveAsinMismatch)}
                        type="button"
                        size="sm"
                        className="px-2 ms-1"
                        title={saveGuards.canSave || saveGuards.canApproveAsinMismatch ? "Save analyzed data to database" : saveGuards.reason}
                    >
                        Save Review Data
                    </Button>
                </InputGroup>
            </Form>
            <Modal
                show={showAsinOverrideConfirm}
                onHide={() => setShowAsinOverrideConfirm(false)}
                centered
                size="sm"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Approve ASIN Mismatch?</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="small">
                        <div className="mb-2">
                            The scraped ASIN does not belong to this book.
                        </div>
                        <div><strong>Expected:</strong> {getExpectedAsinByType(assignmentData?.booksTypeId, assignmentData?.book) || "No ASIN"}</div>
                        <div><strong>Scraped:</strong> {reviewData?.asin || "No ASIN"}</div>
                        <div className="mt-2 text-muted">
                            Confirm only if you manually checked that the review belongs to the same book/content.
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" size="sm" onClick={() => setShowAsinOverrideConfirm(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="danger"
                        size="sm"
                        onClick={() => saveToDb({ staffAsinOverride: true })}
                        disabled={loading}
                    >
                        {loading ? <Spinner animation="border" size="sm" /> : "Approve Anyway"}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* серверный ответ тут же */}
            {serverMsg?.text && (
                <Alert variant={serverMsg.variant || "secondary"} className="py-2 my-2">
                    {serverMsg.text}
                </Alert>
            )}

            {/* локальная ошибка submit */}
            {error && (
                <Alert variant="danger" className="py-2 my-2">
                    {error}
                </Alert>
            )}

            {/* Full info about this Assignment (без записи в БД) */}
            {reviewData && (
                <>


                    <Card className="mt-3 p-2 border border-secondary shadow-sm rounded bg-light">
                        <Card.Body className="pt-2">
                            <h6 className="mt-0 mb-2">Full info about this Assignment:</h6>
                            {/* Основная таблица */}
                            <div className="table-responsive">
                                <table className="table table-sm table-bordered align-middle small mb-2">
                                    <thead className="table-info">
                                    <tr>
                                        <th className="px-2 py-1">Reviewer</th>
                                        <th className="px-2 py-1">ExpR</th>
                                        <th className="px-2 py-1">FindR</th>
                                        <th className="px-2 py-1">Date</th>
                                        <th className="px-2 py-1">Country</th>
                                        <th className="px-2 py-1">ASIN</th>
                                        <th className="px-2 py-1">Type</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    <tr>
                                        <td className="px-2 py-1 fw-semibold">
                                            {assignmentData?.checked_review_name || reviewData?.review_name || "No Name"}
                                        </td>
                                        <td className="px-2 py-1 fw-semibold">
                                            {assignmentData?.rating || "No Rating"}
                                        </td>
                                        <td className="px-2 py-1 fw-semibold">
                                            {reviewData?.review_rating ?? "N/A"}
                                        </td>
                                        <td className="px-2 py-1 fw-semibold">
                                            {reviewData?.review_date || "N/A"}
                                        </td>
                                        <td className="px-2 py-1 fw-semibold">
                                            {reviewData?.review_country || "N/A"}
                                        </td>
                                        <td className="px-2 py-1 fw-semibold text-break">
                                            {reviewData?.asin || "No ASIN"}
                                        </td>
                                        <td className="px-2 py-1 fw-semibold">
                                            {assignmentData?.assTypeId === 8
                                                ? "Audible Review"
                                                : (reviewData?.review_status || "Unverified Purchase")}
                                        </td>
                                    </tr>
                                    </tbody>
                                </table>
                            </div>

                            {/* Title */}
                            <Row className="mt-1">
                                <Col>
                                    <strong className="text-muted mt-1">Title of Review:</strong>
                                    <p className="me-2 mb-1 mt-0 fst-italic text-muted" style={{ lineHeight: "1" }}>
                                        {reviewData?.review_title || "No Title"}
                                    </p>
                                </Col>
                            </Row>

                            {/* Text */}
                            <strong className="text-muted">Text of Review: </strong>
                            <p className="text-dark text-muted fst-italic small" style={{ lineHeight: "1.2" }}>
                                {reviewData?.review_text || "No Text"}
                            </p>

                            {/* Media */}
                            {(reviewData?.review_photo ||
                                reviewData?.review_photo2 ||
                                reviewData?.review_photo3 ||
                                reviewData?.review_photo4 ||
                                reviewData?.review_photo5) && (
                                <>
                                    <strong className="text-muted me-2">Media in Review:</strong>
                                    <div className="d-flex align-items-center flex-nowrap overflow-auto" style={{ gap: 8 }}>
                                        {[
                                            reviewData?.review_photo,
                                            reviewData?.review_photo2,
                                            reviewData?.review_photo3,
                                            reviewData?.review_photo4,
                                            reviewData?.review_photo5,
                                        ]
                                            .filter(Boolean)
                                            .map((u, i) => renderMediaThumb(u, i))}
                                    </div>
                                </>
                            )}

                            {/* Сводка различий */}
                            {(analysis.mismatches.length > 0 || analysis.asinNote) && (
                                <Card className="mt-3 p-0 border border-secondary shadow-sm rounded small">
                                    <Card.Body>
                                        {analysis.asinNote && (
                                            <Alert variant="warning" className="py-1 px-2 small mb-2">
                                                {analysis.asinNote}
                                            </Alert>
                                        )}
                                        {analysis.mismatches.length > 0 && (<>
                                            <h6 className="mt-0 mb-2">Full info about this Assignment:</h6>
                                            <div className="table-responsive">
                                                <table className="table table-sm table-bordered mt-0 align-middle mb-0">
                                                    <thead className="table-danger">
                                                    <tr>
                                                        <th className="px-2 py-1">Field</th>
                                                        <th className="px-2 py-1">Expected</th>
                                                        <th className="px-2 py-1">Received</th>
                                                        <th className="px-2 py-1">Detected mismatches</th>
                                                    </tr>
                                                    </thead>
                                                    <tbody>
                                                    {analysis.mismatches.map((m, i) => (
                                                        <tr key={i} className="fst-italic">
                                                            <td className="px-2 py-1">{m.field}</td>
                                                            <td className="px-2 py-1">{m.expected ?? "—"}</td>
                                                            <td className="px-2 py-1">{m.got ?? "—"}</td>
                                                            <td className="px-2 py-1">{m.note ?? "—"}</td>
                                                        </tr>
                                                    ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                      </>  )}
                                    </Card.Body>
                                </Card>
                            )}
                        </Card.Body>
                    </Card>
                </>
            )}
            </Card.Body>
        </Card>
    );
};

export default AdminAmazonReviewParserExt;
